import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SEOHelmet from '../components/SEOHelmet';

const api = () => axios.create({
 baseURL: '/api',
 headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const StarRating = ({ rating, onChange, size = 'lg', label }) => {
 const [hover, setHover] = useState(0);
 const sizeClass = size === 'lg' ? 'text-2xl' : 'text-base';
 return (
 <div className="flex flex-col gap-1">
 {label && <span className="text-xs text-neutral-500 font-medium">{label}</span>}
 <div className="flex gap-0.5">
 {[1,2,3,4,5].map(star => (
 <button key={star} type="button"
 onMouseEnter={() => onChange && setHover(star)}
 onMouseLeave={() => onChange && setHover(0)}
 onClick={() => onChange && onChange(star)}
 className={`${sizeClass} transition ${onChange ? 'cursor-pointer hover:scale-110' : 'cursor-default'}`}>
 {star <= (hover || rating) ? '⭐' : '☆'}
 </button>
 ))}
 </div>
 </div>
 );
};

const StarBar = ({ count, total, stars }) => {
 const pct = total > 0 ? (count / total) * 100 : 0;
 return (
 <div className="flex items-center gap-2 text-xs">
 <span className="w-4 text-neutral-500 text-right">{stars}</span>
 <span className="text-yellow-400">⭐</span>
 <div className="flex-1 bg-neutral-100 rounded-full h-2">
 <div className="bg-yellow-400 h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
 </div>
 <span className="w-6 text-neutral-400 text-right">{count}</span>
 </div>
 );
};

export default function ReviewsPage() {
 const { userId } = useParams();
 const navigate = useNavigate();
 const http = api();

 const [reviews, setReviews] = useState([]);
 const [stats, setStats] = useState({});
 const [breakdown, setBreakdown] = useState({});
 const [loading, setLoading] = useState(true);
 const [userName, setUserName] = useState('');
 const [userRole, setUserRole] = useState('');

 // Write review
 const [showWrite, setShowWrite] = useState(false);
 const [overall, setOverall] = useState(0);
 const [communication, setCommunication] = useState(0);
 const [professionalism, setProfessionalism] = useState(0);
 const [reliability, setReliability] = useState(0);
 const [safety, setSafety] = useState(0);
 const [reviewText, setReviewText] = useState('');
 const [submitting, setSubmitting] = useState(false);
 const [toast, setToast] = useState('');

 // Response
 const [respondingTo, setRespondingTo] = useState(null);
 const [responseText, setResponseText] = useState('');

 const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

 useEffect(() => { loadReviews(); loadUser(); }, [userId]);

 const loadReviews = async () => {
 try {
 const res = await http.get(`/reviews/${userId}`);
 setReviews(res.data.reviews || []);
 setStats(res.data.stats || {});
 setBreakdown(res.data.breakdown || {});
 } catch { /* silent */ }
 setLoading(false);
 };

 const loadUser = async () => {
 try {
 const res = await http.get(`/worker/${userId}/public`);
 setUserName(res.data.name);
 setUserRole('worker');
 } catch {
 setUserName('User');
 setUserRole('employer');
 }
 };

 const submitReview = async () => {
 if (overall === 0) { setToast('Please select an overall rating'); return; }
 setSubmitting(true);
 try {
 await http.post('/reviews', {
 reviewee_id: userId,
 overall_rating: overall,
 communication_rating: communication || null,
 professionalism_rating: professionalism || null,
 reliability_rating: reliability || null,
 safety_rating: safety || null,
 review_text: reviewText
 });
 setToast('✅ Review submitted!');
 setShowWrite(false);
 setOverall(0); setCommunication(0); setProfessionalism(0); setReliability(0); setSafety(0); setReviewText('');
 loadReviews();
 } catch (err) {
 setToast(err.response?.data?.error || 'Failed to submit');
 }
 setSubmitting(false);
 };

 const submitResponse = async (reviewId) => {
 if (!responseText.trim()) return;
 try {
 await http.post(`/reviews/${reviewId}/respond`, { response_text: responseText });
 setRespondingTo(null); setResponseText('');
 setToast('✅ Response posted');
 loadReviews();
 } catch (err) {
 setToast(err.response?.data?.error || 'Failed to respond');
 }
 };

 const avgRating = parseFloat(stats.avg_overall || 0);
 const totalReviews = parseInt(stats.total_reviews || 0);

 return (
 <div className="min-h-screen bg-neutral-50">
 <SEOHelmet title={`Reviews for ${userName}`} description={`${totalReviews} reviews, ${avgRating} average rating on ShiftMatch`} />

 {/* Header */}
 <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
 <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
 <button onClick={() => navigate(-1)} className="p-2 hover:bg-neutral-100 rounded-lg text-neutral-500">←</button>
 <h1 className="font-bold text-neutral-900">Reviews</h1>
 </div>
 </div>

 <div className="max-w-2xl mx-auto px-4 py-5">
 {loading ? (
 <div className="flex justify-center py-16"><div className="w-7 h-7 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" /></div>
 ) : (
 <>
 {/* Google-style review summary */}
 <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-5 mb-5">
 <div className="flex items-start gap-6">
 {/* Left: Big rating */}
 <div className="text-center flex-shrink-0">
 <div className="text-5xl font-black text-neutral-900">{avgRating > 0 ? avgRating.toFixed(1) : '—'}</div>
 <StarRating rating={Math.round(avgRating)} size="sm" />
 <p className="text-xs text-neutral-400 mt-1">{totalReviews} review{totalReviews !== 1 ? 's' : ''}</p>
 </div>
 {/* Right: Breakdown bars */}
 <div className="flex-1 space-y-1.5">
 {[5,4,3,2,1].map(s => (
 <StarBar key={s} stars={s} count={breakdown[s] || 0} total={totalReviews} />
 ))}
 </div>
 </div>

 {/* Category averages */}
 {totalReviews > 0 && (
 <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t border-neutral-100">
 {[
 { label: 'Communication', val: stats.avg_communication },
 { label: 'Professionalism', val: stats.avg_professionalism },
 { label: 'Reliability', val: stats.avg_reliability },
 { label: 'Safety', val: stats.avg_safety },
 ].map(c => (
 <div key={c.label} className="text-center">
 <div className="text-lg font-bold text-neutral-900">{c.val ? parseFloat(c.val).toFixed(1) : '—'}</div>
 <div className="text-[10px] text-neutral-400">{c.label}</div>
 </div>
 ))}
 </div>
 )}
 </div>

 {/* Actions */}
 <div className="flex gap-2 mb-5">
 <button onClick={() => setShowWrite(!showWrite)}
 className="flex-1 py-2.5 bg-brand-500 text-white font-bold rounded-xl text-sm hover:bg-brand-600 transition">
 ✍️ Write a Review
 </button>
 <button onClick={() => navigate(`/report?type=${userRole}&userId=${userId}&name=${encodeURIComponent(userName)}`)}
 className="py-2.5 px-4 border border-red-200 text-red-600 font-medium rounded-xl text-sm hover:bg-red-50 transition">
 Report
 </button>
 </div>

 {/* Write review form */}
 {showWrite && (
 <div className="bg-white rounded-2xl border border-brand-200 shadow-sm p-5 mb-5">
 <h3 className="font-bold text-neutral-900 mb-4">Write a Review for {userName}</h3>

 <div className="mb-4">
 <label className="text-sm font-medium text-neutral-700 mb-2 block">Overall Rating *</label>
 <StarRating rating={overall} onChange={setOverall} />
 </div>

 <div className="grid grid-cols-2 gap-3 mb-4">
 <div><StarRating rating={communication} onChange={setCommunication} size="sm" label="Communication" /></div>
 <div><StarRating rating={professionalism} onChange={setProfessionalism} size="sm" label="Professionalism" /></div>
 <div><StarRating rating={reliability} onChange={setReliability} size="sm" label="Reliability" /></div>
 <div><StarRating rating={safety} onChange={setSafety} size="sm" label="Safety" /></div>
 </div>

 <textarea value={reviewText} onChange={e => setReviewText(e.target.value)}
 placeholder="Share your experience... What went well? What could be improved?"
 className="w-full px-4 py-3 border border-neutral-200 rounded-xl text-sm resize-none h-24 focus:outline-none focus:border-brand-400 mb-3" />

 <div className="flex gap-2">
 <button onClick={() => setShowWrite(false)} className="flex-1 py-2.5 border border-neutral-200 rounded-xl text-sm font-medium">Cancel</button>
 <button onClick={submitReview} disabled={submitting}
 className="flex-1 py-2.5 bg-brand-500 text-white rounded-xl text-sm font-bold hover:bg-brand-600 disabled:opacity-50">
 {submitting ? 'Posting...' : 'Post Review'}
 </button>
 </div>
 </div>
 )}

 {/* Toast */}
 {toast && (
 <div className={`mb-4 p-3 rounded-xl text-sm font-medium ${toast.startsWith('✅') ? 'bg-brand-50 border border-brand-200 text-brand-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
 {toast}
 </div>
 )}

 {/* Review list */}
 <div className="space-y-3">
 {reviews.length === 0 ? (
 <div className="text-center py-12 bg-white rounded-2xl border border-neutral-100">
 <div className="text-4xl mb-3">⭐</div>
 <p className="font-medium text-neutral-600">No reviews yet</p>
 <p className="text-sm text-neutral-400 mt-1">Be the first to leave a review</p>
 </div>
 ) : reviews.map(r => (
 <div key={r.id} className="bg-white rounded-2xl border border-neutral-100 p-4 shadow-sm">
 <div className="flex items-start justify-between mb-2">
 <div className="flex items-center gap-2">
 <div className="w-8 h-8 bg-neutral-100 rounded-full flex items-center justify-center text-sm font-bold text-neutral-500">
 {(r.reviewer_name || '?')[0].toUpperCase()}
 </div>
 <div>
 <p className="font-semibold text-neutral-900 text-sm">{r.reviewer_name}</p>
 <p className="text-[10px] text-neutral-400">
 {r.reviewer_role === 'worker' ? 'Worker' : 'Employer'} · {new Date(r.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
 </p>
 </div>
 </div>
 <StarRating rating={r.overall_rating} size="sm" />
 </div>

 {r.review_text && (
 <p className="text-sm text-neutral-700 leading-relaxed mb-3">{r.review_text}</p>
 )}

 {/* Sub-ratings */}
 <div className="flex flex-wrap gap-3 text-[10px] text-neutral-400 mb-2">
 {r.communication_rating && <span>Communication: {'⭐'.repeat(r.communication_rating)}</span>}
 {r.reliability_rating && <span>Reliability: {'⭐'.repeat(r.reliability_rating)}</span>}
 {r.professionalism_rating && <span>Professionalism: {'⭐'.repeat(r.professionalism_rating)}</span>}
 {r.safety_rating && <span>Safety: {'⭐'.repeat(r.safety_rating)}</span>}
 </div>

 {/* Owner response */}
 {r.employer_response && (
 <div className="mt-3 bg-neutral-50 rounded-xl p-3 border-l-3 border-brand-400">
 <p className="text-[10px] text-neutral-400 font-semibold mb-1">Response from {userName}</p>
 <p className="text-sm text-neutral-700">{r.employer_response}</p>
 {r.response_at && <p className="text-[10px] text-neutral-300 mt-1">{new Date(r.response_at).toLocaleDateString('en-AU')}</p>}
 </div>
 )}

 {/* Respond button (only for the reviewee) */}
 {!r.employer_response && currentUser?.id === userId && (
 <>
 {respondingTo === r.id ? (
 <div className="mt-3 flex gap-2">
 <input value={responseText} onChange={e => setResponseText(e.target.value)}
 placeholder="Write your response..."
 className="flex-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-brand-400" />
 <button onClick={() => submitResponse(r.id)} className="px-3 py-2 bg-brand-500 text-white rounded-lg text-xs font-bold">Post</button>
 <button onClick={() => setRespondingTo(null)} className="px-3 py-2 border border-neutral-200 rounded-lg text-xs">Cancel</button>
 </div>
 ) : (
 <button onClick={() => setRespondingTo(r.id)}
 className="mt-2 text-xs text-brand-600 font-medium hover:underline">
 Reply to this review
 </button>
 )}
 </>
 )}
 </div>
 ))}
 </div>
 </>
 )}
 </div>

 <footer className="text-center py-5 text-[10px] text-neutral-400 border-t border-neutral-100 mt-6">
 © 2026 ShiftMatch Australia · <a href="/terms.html" className="hover:text-brand-600">Terms</a> · <a href="/privacy.html" className="hover:text-brand-600">Privacy</a>
 </footer>
 </div>
 );
}
